import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { LocationAutocomplete } from '@/components/ui/location-autocomplete';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useUsers } from '@/hooks/use-users';
import { cn } from '@/lib/utils';
import {
  type Appointment,
  type CreateAppointmentRequest,
  createLocalizedAppointmentSchema,
  type LocationDetails,
} from '@/types/appointment';

interface AppointmentFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateAppointmentRequest) => void;
  appointment?: Appointment;
  isLoading?: boolean;
  mode: 'create' | 'edit';
}

export function AppointmentForm({ open, onOpenChange, onSubmit, appointment, isLoading, mode }: AppointmentFormProps) {
  const { t } = useTranslation();
  const [date, setDate] = useState<Date>();
  const [timeInput, setTimeInput] = useState('');
  const { data: usersData } = useUsers();
  const users = usersData?.users ?? {};

  const form = useForm<CreateAppointmentRequest>({
    resolver: zodResolver(createLocalizedAppointmentSchema(t)),
    defaultValues: {
      title: '',
      location: '',
      locationDetails: undefined,
      startTime: '',
      duration: 60,
      description: '',
      assignedTo: undefined,
    },
  });

  const handleLocationChange = (value: string, details?: LocationDetails) => {
    form.setValue('location', value);
    form.setValue('locationDetails', details);
  };

  // Set form values when appointment changes (edit mode)
  useEffect(() => {
    if (appointment && mode === 'edit') {
      const appointmentDate = new Date(appointment.startTime);
      const hours = appointmentDate.getHours();
      const minutes = appointmentDate.getMinutes();

      // Round to nearest 15-minute interval
      const roundedMinutes = Math.round(minutes / 15) * 15;
      let adjustedHours = roundedMinutes >= 60 ? hours + 1 : hours;
      const finalMinutes = roundedMinutes >= 60 ? roundedMinutes - 60 : roundedMinutes;

      // Clamp to 6am-8pm range
      if (adjustedHours < 6) {
        adjustedHours = 6;
      } else if (adjustedHours > 20) {
        adjustedHours = 20;
      }

      const timeString = `${adjustedHours.toString().padStart(2, '0')}:${finalMinutes.toString().padStart(2, '0')}`;

      setDate(appointmentDate);
      setTimeInput(timeString);
      form.reset({
        title: appointment.title,
        location: appointment.location,
        locationDetails: appointment.locationDetails,
        startTime: timeString,
        duration: appointment.duration,
        description: appointment.description || '',
        assignedTo: appointment.assignedTo,
      });
    }
  }, [appointment, form, mode]);

  const handleSubmit = (data: CreateAppointmentRequest) => {
    if (date && data.startTime) {
      const [hours, minutes] = data.startTime.split(':');
      const appointmentDate = new Date(date);
      appointmentDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

      onSubmit({
        ...data,
        startTime: appointmentDate.toISOString(),
      });
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset();
      setDate(undefined);
      setTimeInput('');
    }
    onOpenChange(newOpen);
  };

  const isCreateMode = mode === 'create';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isCreateMode ? t('appointments.createTitle') : t('appointments.editTitle')}</DialogTitle>
          <DialogDescription>
            {isCreateMode ? t('appointments.createDescription') : t('appointments.editDescription')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('appointments.title')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('appointments.titlePlaceholder')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('appointments.location')}</FormLabel>
                  <FormControl>
                    <LocationAutocomplete
                      value={field.value}
                      onChange={handleLocationChange}
                      placeholder={t('appointments.locationPlaceholder')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormItem>
                <FormLabel>{t('appointments.date')}</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn('w-full pl-3 text-left font-normal', !date && 'text-muted-foreground')}
                      >
                        {date ? format(date, 'PPP') : <span>{t('appointments.selectDate')}</span>}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today;
                      }}
                      autoFocus
                    />
                  </PopoverContent>
                </Popover>
              </FormItem>

              <FormField
                control={form.control}
                name="startTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('appointments.time')}</FormLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        setTimeInput(value);
                      }}
                      value={timeInput || field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('appointments.selectTime')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Array.from({ length: 57 }, (_, i) => {
                          const totalMinutes = 6 * 60 + i * 15; // Start at 6:00 AM
                          const hours = Math.floor(totalMinutes / 60);
                          const minutes = totalMinutes % 60;

                          // Stop at 8:00 PM (20:00)
                          if (hours > 20) return null;

                          const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                          return (
                            <SelectItem key={timeString} value={timeString}>
                              {timeString}
                            </SelectItem>
                          );
                        }).filter(Boolean)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="duration"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('appointments.duration')}</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(parseInt(value, 10))}
                    value={field.value?.toString()}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('appointments.selectDuration')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="15">15 {t('time.minutes')}</SelectItem>
                      <SelectItem value="30">30 {t('time.minutes')}</SelectItem>
                      <SelectItem value="45">45 {t('time.minutes')}</SelectItem>
                      <SelectItem value="60">1 {t('time.hour')}</SelectItem>
                      <SelectItem value="90">1.5 {t('time.hours')}</SelectItem>
                      <SelectItem value="120">2 {t('time.hours')}</SelectItem>
                      <SelectItem value="180">3 {t('time.hours')}</SelectItem>
                      <SelectItem value="240">4 {t('time.hours')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="assignedTo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('appointments.assignTo')}</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(value === '_unassigned' ? undefined : value)}
                    value={field.value || '_unassigned'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('appointments.selectAssignee')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="_unassigned">{t('appointments.unassigned')}</SelectItem>
                      {Object.values(users).map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name || user.email || user.id}
                          {user.status === 'pre-created' && (
                            <span className="text-muted-foreground ml-1">({t('users.pending')})</span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>{t('appointments.assignToDescription')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('appointments.description')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('appointments.descriptionPlaceholder')}
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>{t('appointments.descriptionOptional')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                {t('forms.cancel')}
              </Button>
              <Button type="submit" disabled={isLoading || !date || !form.getValues('startTime')}>
                {isLoading
                  ? isCreateMode
                    ? t('forms.creating')
                    : t('forms.updating')
                  : isCreateMode
                    ? t('forms.create')
                    : t('forms.update')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
